import {observer} from 'mobx-react-lite'
import React, {useEffect, useLayoutEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Tag} from 'yougile-model'
import {Sync} from '@client/sync'
import {boardSettingsStore} from '@components/board/board-settings/board-settings-store'
import {LinkButtonNew} from '@components/common/base/buttons'
import {ActionsPanel} from '@components/common/base/buttons/actions-panel/actions-panel'
import {Icon} from '@components/common/base/icon'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {StickerSettingsContent} from '@components/sticker/my-stickers/board-stickers/sticker-settings/sticker-settings-content'
import {TwTitle} from '@components/sticker/my-stickers/containers'
import {useMyStickersStore} from '@components/sticker/my-stickers/my-stickers-store'
import {NumericStates} from '@components/sticker/my-stickers/sticker-editor/numeric-states'
import {SprintStates} from '@components/sticker/my-stickers/sticker-editor/sprint-states/sprint-states'
import {
  StickerEditorContext,
  StickerEditorStore,
  useStickerEditorStore
} from '@components/sticker/my-stickers/sticker-editor/sticker-editor-store'
import {StickerName} from '@components/sticker/my-stickers/sticker-editor/sticker-name'
import {StringStates} from '@components/sticker/my-stickers/sticker-editor/string-states'
import {TextStates} from '@components/sticker/my-stickers/sticker-editor/text-states'
import {useCompanyId} from '@containers/hooks'
import {Sticker} from '@extensions/sticker/sticker'

const TwTruncate = tw.span`
  inline-block
  truncate
  max-w-160
  align-bottom
`

const EditingStickerBase = observer(() => {
  const [state, store] = useStickerEditorStore()

  const edited = store.edited
  useLayoutEffect(() => {
    store.checkEdited()
  }, [edited])

  const companyId = useCompanyId()
  const company = Sync.getCurrentSandbox().getHub(companyId)

  if (!state.sticker) {
    return null
  }

  const name = Sticker.getName(state.sticker)

  return (
    <div className="relative flex flex-col h-full">
      <WithPerfectScrollbar className="relative pb-[80px] h-full flex flex-col gap-8 pr-16 -mr-16">
        <TwTitle className="pb-16">
          <LinkButtonNew
            icon="IconArrowLeft"
            title={Lang.msg.myStickers.backToBoardStickers}
            onClick={store.tryGoBack}
          />
        </TwTitle>

        <StickerSettingsContent />

        {Sticker.isCustom(state.sticker) && (
          <>
            <div className="flex flex-col mt-24">
              <div className="py-8 text-sm-semibold text-primary">
                {Lang.msg.myStickers.editingSticker1}{' '}
                <TwTruncate>{name}</TwTruncate>{' '}
                {Lang.msg.myStickers.editingSticker2}{' '}
                <TwTruncate>{company?.title}</TwTruncate>
              </div>

              <div className="py-8 text-sm-regular text-status-error">
                <Icon
                  icon="IconAlertCircle"
                  color="status-error"
                  className="!inline-flex align-bottom mr-4"
                />
                {Lang.msg.myStickers.editingWarning1}{' '}
                <TwTruncate>{name}</TwTruncate>{' '}
                <span className="font-semibold">
                  {Lang.msg.myStickers.editingWarning2}
                </span>{' '}
                {Lang.msg.myStickers.editingWarning3}
              </div>

              <StickerName />
            </div>

            {state.stickerType === 'string' && <StringStates />}
            {state.stickerType === 'sprint' && <SprintStates />}
            {state.stickerType === 'text' && <TextStates />}
            {state.stickerType === 'numeric' && <NumericStates />}
          </>
        )}
      </WithPerfectScrollbar>

      <ActionsPanel
        className="!bottom-8"
        show={edited}
        primaryClick={store.trySave}
        primaryText={Lang.msg.save}
        secondaryText={Lang.msg.cancel}
        secondaryClick={store.cancel}
        error={store.hasErrors}
        errorText={store.errorText}
        errorBg={state.errorBg}
      />
    </div>
  )
})

export const EditingSticker = observer(() => {
  const [, stickersStore] = useMyStickersStore()
  const [store] = useState(() => {
    const store = new StickerEditorStore(stickersStore, boardSettingsStore)
    const initialSticker = stickersStore.state.selectedSticker as Tag
    store.initForExistingSticker(initialSticker)
    return store
  })

  useEffect(() => store.reset, [store])

  return (
    <StickerEditorContext.Provider value={store}>
      <EditingStickerBase />
    </StickerEditorContext.Provider>
  )
})
